/**
 * <product-info> — product page + quick view behaviour.
 *
 * Hooks (all optional, found inside the element):
 *   [data-variants]                 <script type="application/json"> product.variants
 *   [data-option-input]             radio per option value (fieldset[data-option-index])
 *   [data-gallery] [data-gallery-track] [data-gallery-thumb] [data-gallery-counter] [data-gallery-dot]
 *   [data-bundle-ladder] [data-tier-input data-tier-qty data-tier-permille] [data-tier-total] [data-tier-per]
 *     Numbered-variant ladder ([data-numbered]): each tier input also carries data-tier-variant + data-tier-price (cents);
 *     the tier's variant is added with quantity 1; the "Beli 1" variant price is the baseline for the savings line.
 *   [data-bundle-summary] [data-bundle-save]
 *   [data-qty-step] [data-qty-value] [data-quantity-input] [data-id-input]
 *   [data-price] [data-compare] [data-save-badge] [data-sale-badge]
 *   [data-add-button] (data-label-add / data-label-sold-out) [data-add-label]
 *   [data-buy-now]                  add to cart, then go to checkout
 * Events: dispatches `product:changed` (bubbles) with detail { total, totalFormatted, selection, available, variantId }.
 * Also defines <delivery-estimate> (cut-off countdown + arrival range).
 */
(() => {
  const RM = (window.RM = window.RM || {});

  const group = (n, sep) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, sep);

  /** Format cents with the store money format (placeholders {{amount}} etc.), trailing ".00" removed. */
  RM.formatMoney = (cents, format) => {
    const value = Number(cents) / 100;
    if (!format) {
      // No store format supplied: fall back to Intl with the active Shopify currency.
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'MYR';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
        }).format(value);
      } catch (e) {
        return `${currency} ${value.toFixed(2).replace(/\.00$/, '')}`;
      }
    }
    const fmt = format;
    const two = value.toFixed(2);
    const [whole, dec] = two.split('.');
    const map = {
      amount: `${group(whole, ',')}.${dec}`,
      amount_no_decimals: group(Math.round(value), ','),
      amount_with_comma_separator: `${group(whole, '.')},${dec}`,
      amount_no_decimals_with_comma_separator: group(Math.round(value), '.'),
      amount_with_apostrophe_separator: `${group(whole, "'")}.${dec}`,
      amount_no_decimals_with_space_separator: group(Math.round(value), ' '),
      amount_with_space_separator: `${group(whole, ' ')},${dec}`,
    };
    return fmt
      .replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => (key in map ? map[key] : m))
      .replace(/[.,]00(?=\D|$)/, '');
  };

  class ProductInfo extends HTMLElement {
    #abort = new AbortController();

    connectedCallback() {
      const { signal } = this.#abort;
      this.format = this.dataset.moneyFormat || '';
      this.qty = 1;
      this.tierQty = 1;
      this.tierPermille = 0;
      this.cache = {};

      const json = this.querySelector('script[data-variants]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.idInput = this.querySelector('[data-id-input]');
      this.qtyInput = this.querySelector('[data-quantity-input]');
      this.qtyValue = this.querySelector('[data-qty-value]');
      this.addButton = this.querySelector('[data-add-button]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.buyNow = this.querySelector('[data-buy-now]');
      this.ladder = this.querySelector('[data-bundle-ladder]');

      const selectedId = this.idInput ? Number(this.idInput.value) : null;
      this.variant = this.variants.find((v) => v.id === selectedId) || this.variants[0] || null;
      this.maxQty = Number(this.qtyValue?.dataset.max) || 99;

      this.#initGallery();

      this.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (target.matches('[data-option-input]')) this.#onOption();
        if (target.matches('[data-tier-input]')) this.#onTier(target);
      }, { signal });

      this.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        const step = target.closest('[data-qty-step]');
        if (step) {
          this.qty = Math.min(this.maxQty, Math.max(1, this.qty + Number(step.dataset.qtyStep)));
          this.#render();
          return;
        }
        if (target.closest('[data-buy-now]')) this.#buyNow();
      }, { signal });

      // Scoped to this <product-info>: the sticky bar bubbles its event from inside, so a quick-view never double-adds.
      this.addEventListener('sticky-atc:add', () => this.addButton?.click(), { signal });

      this.numbered = Boolean(this.ladder && this.ladder.dataset.numbered === 'true');
      if (this.numbered) {
        // Price block shows the single-item ("Beli 1") variant; the selected tier variant is what gets added to the cart.
        const one = this.ladder.querySelector('[data-tier-input][data-tier-qty="1"][data-tier-variant]');
        this.unitVariant = one ? this.variants.find((v) => v.id === Number(one.dataset.tierVariant)) || null : null;
        let selected = this.querySelector('[data-tier-input]:checked:not(:disabled)');
        if (!selected) {
          selected = this.querySelector('[data-tier-input]:not(:disabled)');
          if (selected) selected.checked = true;
        }
        if (selected) this.#readTier(selected);
      } else {
        const checked = this.querySelector('[data-tier-input]:checked');
        if (checked) this.#readTier(checked);
      }
      this.#render(true);
    }

    disconnectedCallback() {
      this.#abort.abort();
    }

    /* ---------- gallery ---------- */
    #initGallery() {
      const { signal } = this.#abort;
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      this.activeIndex = 0;

      for (const thumb of this.thumbs) {
        thumb.addEventListener('click', () => this.#goTo(Number(thumb.dataset.index)), { signal });
      }
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#goTo(this.activeIndex - 1), { signal });
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#goTo(this.activeIndex + 1), { signal });

      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const width = this.track.clientWidth || 1;
          this.#setActive(Math.round(this.track.scrollLeft / width));
        });
      }, { signal, passive: true });

      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.#goTo(this.activeIndex + 1);
        if (event.key === 'ArrowLeft') this.#goTo(this.activeIndex - 1);
      }, { signal });
    }

    #goTo(index) {
      if (!this.slides || this.slides.length === 0) return;
      const next = Math.max(0, Math.min(this.slides.length - 1, index));
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      this.#setActive(next);
    }

    #setActive(index) {
      if (!this.slides || index === this.activeIndex || index < 0 || index >= this.slides.length) return;
      this.activeIndex = index;
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    }

    /* ---------- variants ---------- */
    #onOption() {
      const values = [...this.querySelectorAll('fieldset[data-option-index]')].map((fieldset) => {
        const checked = fieldset.querySelector('[data-option-input]:checked');
        const label = fieldset.querySelector('[data-option-current]');
        if (label && checked) label.textContent = checked.value;
        return checked ? checked.value : null;
      });
      const match = this.variants.find((v) => v.options.every((opt, i) => opt === values[i]));
      this.variant = match || null;
      if (match) {
        if (this.idInput) this.idInput.value = String(match.id);
        const mediaId = match.featured_media && match.featured_media.id;
        if (mediaId && this.slides) {
          const index = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === mediaId);
          if (index > -1) this.#goTo(index);
        }
        if (this.dataset.updateUrl === 'true') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(match.id));
          window.history.replaceState({}, '', url.toString());
        }
      }
      this.#render();
    }

    /* ---------- bundle ---------- */
    #readTier(input) {
      this.tierQty = Number(input.dataset.tierQty) || 1;
      this.tierPermille = Number(input.dataset.tierPermille) || 0;
      this.tierSub = input.dataset.tierSub || '';
      if (this.numbered) {
        const tierVariant = this.variants.find((v) => v.id === Number(input.dataset.tierVariant));
        if (tierVariant) {
          this.variant = tierVariant;
          if (this.idInput) this.idInput.value = String(tierVariant.id);
        }
      }
      for (const card of this.querySelectorAll('.pdp-bcard')) {
        card.classList.toggle('is-selected', card.contains(input));
      }
    }

    #onTier(input) {
      this.#readTier(input);
      this.#render();
    }

    #money(cents) {
      return RM.formatMoney(cents, this.format);
    }

    /* ---------- render ---------- */
    #render(initial) {
      const variant = this.variant;
      const numbered = this.numbered;
      // Numbered ladder: the chosen tier IS a variant (its price is the real bundle price), so the quantity stays at 1 x stepper.
      const priceVariant = numbered && this.unitVariant ? this.unitVariant : variant;
      const unit = priceVariant ? priceVariant.price : 0;
      const tierTotal = (qty, permille) => Math.round(unit * qty * (1000 - permille) / 1000);
      const selectionTotal = numbered
        ? (variant ? variant.price : 0) * this.qty
        : tierTotal(this.tierQty, this.tierPermille) * this.qty;
      const available = Boolean(variant && variant.available);

      if (this.qtyValue) this.qtyValue.textContent = String(this.qty);
      if (this.qtyInput) this.qtyInput.value = String(numbered ? this.qty : this.tierQty * this.qty);

      if (priceVariant) {
        const price = this.querySelector('[data-price]');
        const compare = this.querySelector('[data-compare]');
        const save = this.querySelector('[data-save-badge]');
        const sale = this.querySelector('[data-sale-badge]');
        if (price) price.textContent = this.#money(priceVariant.price);
        const onSale = priceVariant.compare_at_price && priceVariant.compare_at_price > priceVariant.price;
        if (compare) {
          compare.hidden = !onSale;
          if (onSale) compare.textContent = this.#money(priceVariant.compare_at_price);
        }
        if (save) {
          save.hidden = !onSale;
          if (onSale) {
            const template = save.dataset.template;
            save.textContent = template
              ? template.replace('__AMOUNT__', this.#money(priceVariant.compare_at_price - priceVariant.price))
              : this.#money(priceVariant.compare_at_price - priceVariant.price);
          }
        }
        if (sale) {
          sale.hidden = !onSale;
          if (onSale) sale.textContent = `-${Math.round((priceVariant.compare_at_price - priceVariant.price) * 100 / priceVariant.compare_at_price)}%`;
        }
      }

      // Bundle cards
      let summaryText = '';
      let saveText = '';
      if (this.ladder && variant) {
        const unitOne = this.ladder.dataset.unitOne || '';
        const unitOther = this.ladder.dataset.unitOther || unitOne;
        const template = this.ladder.dataset.saveTemplate || '';
        for (const input of this.ladder.querySelectorAll('[data-tier-input]')) {
          const qty = Number(input.dataset.tierQty) || 1;
          const permille = Number(input.dataset.tierPermille) || 0;
          const total = numbered ? Number(input.dataset.tierPrice) || 0 : tierTotal(qty, permille);
          const card = input.closest('.pdp-bcard');
          const totalEl = card && card.querySelector('[data-tier-total]');
          const perEl = card && card.querySelector('[data-tier-per]');
          if (totalEl) totalEl.textContent = this.#money(total);
          if (perEl) {
            const per = this.#money(Math.round(total / qty));
            const current = perEl.textContent || '';
            // "RM39 / bottle" keeps its unit; a unit-less card is just the price.
            perEl.textContent = current.includes('/') ? current.replace(/^[^/]*\//, `${per} /`) : per;
          }
        }
        const count = this.tierQty * this.qty;
        summaryText = `${count} ${count === 1 ? unitOne : unitOther}${this.tierSub ? ` · ${this.tierSub}` : ''}`;
        // Numbered ladder: savings only when N x the single-item variant price beats the bundle variant price.
        const was = unit * count;
        const saved = numbered && !this.unitVariant ? 0 : was - selectionTotal;
        if (saved > 0 && template) saveText = template.replace('__AMOUNT__', this.#money(saved)).replace('__WAS__', this.#money(was));
        const summary = this.querySelector('[data-bundle-summary]');
        const saveEl = this.querySelector('[data-bundle-save]');
        if (summary) summary.textContent = summaryText;
        if (saveEl) saveEl.textContent = saveText;
      }

      // Buttons
      if (this.addButton) {
        this.addButton.disabled = !available;
        if (this.addLabel) {
          this.addLabel.textContent = available
            ? `${this.addButton.dataset.labelAdd} · ${this.#money(selectionTotal)}`
            : this.addButton.dataset.labelSoldOut;
        }
      }
      if (this.buyNow) this.buyNow.disabled = !available;

      this.state = {
        total: selectionTotal,
        totalFormatted: this.#money(selectionTotal),
        selection: summaryText || (variant && variant.title !== 'Default Title' ? variant.title : ''),
        available,
        variantId: variant ? variant.id : null,
        initial: Boolean(initial),
      };
      this.dispatchEvent(new CustomEvent('product:changed', { bubbles: true, detail: this.state }));
    }

    async #buyNow() {
      if (!this.variant || !this.variant.available) return;
      const button = this.buyNow;
      button.setAttribute('aria-busy', 'true');
      try {
        await RM.addToCart(
          [{ id: this.variant.id, quantity: this.numbered ? this.qty : this.tierQty * this.qty }],
          { source: 'buy-now', openDrawer: false },
        );
        window.location.href = button.dataset.checkoutUrl || '/checkout';
      } catch (error) {
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { message: error.message } }));
        button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);

  /**
   * <delivery-estimate data-cutoff-hour="15" data-min-days="1" data-max-days="3" data-text="Order in the next {countdown}, arrives {range}" data-region="(West Malaysia)">
   * Cut-off in Malaysia time (UTC+8), working days Mon–Fri only.
   */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.cutoff = Number(this.dataset.cutoffHour) || 15;
      this.min = Number(this.dataset.minDays) || 1;
      this.max = Number(this.dataset.maxDays) || 3;
      this.template = this.dataset.text || 'Order in the next {countdown}, arrives {range}';
      this.built = false;
      this.region = this.dataset.region || '';
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const DAY = 86400000;
      const isWorkday = (ms) => {
        const d = new Date(ms).getUTCDay();
        return d !== 0 && d !== 6;
      };
      const addWorkdays = (ms, n) => {
        let cursor = ms;
        let count = 0;
        while (count < n) {
          cursor += DAY;
          if (isWorkday(cursor)) count += 1;
        }
        return cursor;
      };
      const now = Date.now();
      const myt = new Date(now + 8 * 3600000);
      // Cut-off expressed in UTC: (cutoff - 8):00 on the Malaysian calendar day.
      let cut = Date.UTC(myt.getUTCFullYear(), myt.getUTCMonth(), myt.getUTCDate(), this.cutoff - 8, 0, 0);
      if (now >= cut) cut += DAY;
      while (!isWorkday(cut)) cut += DAY;
      const diff = Math.max(0, Math.floor((cut - now) / 1000));
      const pad = (n) => String(n).padStart(2, '0');
      const countdown = `${pad(Math.floor(diff / 3600))}:${pad(Math.floor((diff % 3600) / 60))}:${pad(diff % 60)}`;

      const first = addWorkdays(cut, this.min);
      const last = addWorkdays(cut, this.max);
      const locale = document.documentElement.lang || 'en';
      const fmt = (ms, withMonth) => new Intl.DateTimeFormat(locale, {
        weekday: 'short', day: 'numeric', ...(withMonth ? { month: 'short' } : {}), timeZone: 'UTC',
      }).format(ms);
      const sameMonth = new Date(first).getUTCMonth() === new Date(last).getUTCMonth();
      const range = this.min === this.max ? fmt(first, true) : `${fmt(first, !sameMonth)} – ${fmt(last, true)}`;

      if (!this.built) {
        // Build the DOM once; afterwards only the countdown text node changes.
        const [before, afterCountdown] = this.template.split('{countdown}');
        const parts = (afterCountdown || '').split('{range}');
        this.replaceChildren();
        this.append(document.createTextNode(`${before || ''}`));
        this.countdownEl = document.createElement('b');
        this.countdownEl.className = 'pdp-delivery__countdown';
        this.rangeEl = document.createElement('b');
        this.append(this.countdownEl, document.createTextNode(parts[0] || ''), this.rangeEl, document.createTextNode(`${parts[1] || ''} ${this.region}`.trimEnd()));
        this.built = true;
      }
      if (this.countdownEl.textContent !== countdown) this.countdownEl.textContent = countdown;
      if (this.rangeEl.textContent !== range) this.rangeEl.textContent = range;
    }
  }

  if (!customElements.get('delivery-estimate')) customElements.define('delivery-estimate', DeliveryEstimate);
})();
